import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { toast } from "sonner";

import { MODES, MODE_META } from "../lib/modes";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const MODE_KEY = "codingdrills_mode";
export const CLASS_MODE_KEY = "codingdrills_class_mode";
export { MODES };

const ModeContext = createContext({ mode: "python", setMode: () => {}, hasChosen: true });

const readStored = () => {
  try {
    const v = localStorage.getItem(MODE_KEY);
    return MODES.includes(v) ? v : null;
  } catch {
    return null;
  }
};

export const ModeProvider = ({ user, children }) => {
  const [stored, setStored] = useState(readStored);
  const syncedForUser = useRef(null);

  // On login: the choice already made in this browser wins (and is saved to the account);
  // a device with no choice yet adopts the account's saved preference.
  useEffect(() => {
    if (!user?.user_id || syncedForUser.current === user.user_id) return;
    syncedForUser.current = user.user_id;
    if (stored) {
      if (user.preferred_mode !== stored) {
        fetch(`${API}/user/preferences`, {
          method: "PATCH",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ preferred_mode: stored }),
        }).catch(() => {});
      }
    } else if (MODES.includes(user.preferred_mode)) {
      localStorage.setItem(MODE_KEY, user.preferred_mode);
      setStored(user.preferred_mode);
    }
  }, [user, stored]);

  const setMode = useCallback((next) => {
    if (!MODES.includes(next)) return;
    localStorage.setItem(MODE_KEY, next);
    setStored(next);
    if (user?.user_id) {
      fetch(`${API}/user/preferences`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ preferred_mode: next }),
      }).catch(() => {});
    }
  }, [user]);

  // Class default: a teacher's choice moves the student into that mode ONCE per (class, mode, time it was set);
  // afterwards the student is free to switch from the header.
  const classMarkerRef = useRef(null);
  useEffect(() => {
    const cd = user?.class_default_mode;
    if (!user?.user_id || !cd?.mode || !MODES.includes(cd.mode)) return;
    const key = `${CLASS_MODE_KEY}_${user.user_id}`;
    const marker = `${cd.class_id}:${cd.mode}:${cd.set_at || ""}`;
    if (classMarkerRef.current === marker) return;
    classMarkerRef.current = marker;
    try {
      if (localStorage.getItem(key) === marker) return;
      localStorage.setItem(key, marker);
    } catch {
      return;
    }
    if (stored !== cd.mode) {
      setMode(cd.mode);
      toast(`${cd.class_name || "Your class"} uses ${MODE_META[cd.mode].label} - we've switched you over. Change it any time from the header.`, { duration: 7000 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id, user?.class_default_mode?.class_id, user?.class_default_mode?.mode, user?.class_default_mode?.set_at]);

  return (
    <ModeContext.Provider value={{ mode: stored || "python", setMode, hasChosen: !!stored, draftOwner: user?.user_id || "anon" }}>
      {children}
    </ModeContext.Provider>
  );
};

export const useMode = () => useContext(ModeContext);

// Storage-key helper so anonymous progress is tracked separately per mode
export const modeKey = (mode, key) => (mode && mode !== "python" ? `${mode}_${key}` : key);
